import { z } from 'zod';
import { ScenarioExecutionSchema, ScenarioLinkedFlowSchema } from './scenarios';
import { ScenarioAutomationCoverageSchema, ScenarioAutomationStepMappingSchema } from './scenario-automation-map';
import { AutomationTraceModeSchema } from './trace';

/**
 * Requirement #9 — Quality Scenario → existing automation run orchestration.
 * Does not introduce a second runner; links quality_scenario_executions → test_runs.
 */

export const SCENARIO_EXECUTION_KIND = ['manual', 'automation'] as const;
export type ScenarioExecutionKind = (typeof SCENARIO_EXECUTION_KIND)[number];

/** v0.46 explicit execution target override for scenario automation runs. */
export const ScenarioExecutionTargetRequestSchema = z.object({
  kind: z.enum(['saas', 'agent', 'cloud']),
  /** When kind is `agent`, pin a specific runner token; omit to allow any healthy agent. */
  tokenId: z.string().uuid().optional(),
});
export type ScenarioExecutionTargetRequest = z.infer<typeof ScenarioExecutionTargetRequestSchema>;

export const StartScenarioAutomationExecutionRequestSchema = z.object({
  kind: z.literal('automation').optional(),
  /** When mapping is stale, require explicit confirm before starting. */
  confirmStale: z.boolean().optional(),
  /** Prefer a specific linked flow; default = primary convert/mapped flow. */
  flowId: z.string().uuid().optional(),
  environment: z.string().max(80).optional(),
  browser: z.string().max(40).optional(),
  /** QD-1 Prerequisite #1 — optional CI build identity (never manufactured). */
  buildId: z.string().trim().min(1).max(120).nullable().optional(),
  commitSha: z.string().trim().min(7).max(64).nullable().optional(),
  /**
   * `auto` — resolve via `resolveExecutionTarget()` ladder (v0.46 default).
   * `now` — invoke shared dispatcher → existing mvp StepProcessor (no Flow Builder required).
   * `queue` — reserve runId only until a worker/dispatcher is available.
   * `client` — FE/useRunController drives mvp (compat path).
   */
  dispatch: z.enum(['now', 'queue', 'client', 'auto']).optional(),
  /** Optional explicit target when dispatch is `auto`. Ignored for `now` (SaaS headless). */
  executionTarget: ScenarioExecutionTargetRequestSchema.optional(),
  /** v0.46.1 — Playwright trace capture for automation runs (default off). */
  traceMode: AutomationTraceModeSchema.optional(),
});
export type StartScenarioAutomationExecutionRequest = z.infer<typeof StartScenarioAutomationExecutionRequestSchema>;

export const ScenarioAutomationRunPrepareSchema = z.object({
  maxRunMs: z.number().optional(),
  setup: z.unknown().optional(),
  teardown: z.unknown().optional(),
  phaseTags: z.unknown().optional(),
  locatorCache: z.unknown().optional(),
});
export type ScenarioAutomationRunPrepare = z.infer<typeof ScenarioAutomationRunPrepareSchema>;

export const ScenarioAutomationDispatchResultSchema = z.object({
  status: z.enum(['completed', 'queued', 'unavailable']),
  reason: z.string().optional(),
  resultStatus: z.enum(['passed', 'failed', 'cancelled']).optional(),
  enginePath: z.literal('step-processor').optional(),
});
export type ScenarioAutomationDispatchResult = z.infer<typeof ScenarioAutomationDispatchResultSchema>;

export const StartScenarioAutomationExecutionResponseSchema = z.object({
  execution: ScenarioExecutionSchema,
  flowId: z.string().uuid(),
  flowName: z.string(),
  runId: z.string().uuid(),
  scenarioVersion: z.number().int().positive(),
  stale: z.boolean(),
  coverage: ScenarioAutomationCoverageSchema.optional(),
  mappings: z.array(ScenarioAutomationStepMappingSchema).optional(),
  /** Existing /api/runs/prepare payload — FE reuses useRunController path. */
  prepare: ScenarioAutomationRunPrepareSchema.optional(),
  linked: z.array(ScenarioLinkedFlowSchema).optional(),
  /** Outcome of the shared dispatcher when dispatch=now. */
  dispatch: ScenarioAutomationDispatchResultSchema.optional(),
});
export type StartScenarioAutomationExecutionResponse = z.infer<typeof StartScenarioAutomationExecutionResponseSchema>;

export const CancelScenarioExecutionRequestSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type CancelScenarioExecutionRequest = z.infer<typeof CancelScenarioExecutionRequestSchema>;

export const SyncScenarioExecutionFromRunRequestSchema = z.object({
  runId: z.string().uuid(),
  status: z.enum(['running', 'passed', 'failed', 'cancelled', 'queued']),
  failureSummary: z.string().max(2_000).optional(),
});
export type SyncScenarioExecutionFromRunRequest = z.infer<typeof SyncScenarioExecutionFromRunRequestSchema>;
